import type { MD3Theme } from 'react-native-paper';
import type { DriverWorkState, LimitLevel } from './types';

// Colours for the work states and the limit levels. The app theme has no
// success/warning roles, so these are fixed pairs checked for WCAG AA
// (text on container ≥ 4.5:1; accent on the theme surface ≥ 4.5:1).
// Amber and red are kept for limit warnings only, so a state never looks like
// a warning.

export interface ToneColors {
  container: string;
  onContainer: string;
  accent: string;
}

const STATE_TONES: Record<Exclude<DriverWorkState, 'resting'>, { light: ToneColors; dark: ToneColors }> = {
  driving: {
    light: { container: '#DCEBFA', onContainer: '#00365C', accent: '#0A5C99' },
    // Brand blue in dark mode (Ronald, 2026-10-30), same family as brand A dark.
    dark: { container: '#003A5E', onContainer: '#D6ECFB', accent: '#7CC4F2' },
  },
  available: {
    light: { container: '#DDF3E4', onContainer: '#0B3D1F', accent: '#1B6B36' },
    dark: { container: '#133D24', onContainer: '#CFF5DC', accent: '#7FD69C' },
  },
};

const WARNING_TONE = {
  light: { container: '#FFEBC7', onContainer: '#4A2E00', accent: '#8A4F00' },
  dark: { container: '#4A3300', onContainer: '#FFE3B0', accent: '#FFC266' },
};

export const stateTone = (state: DriverWorkState, theme: MD3Theme): ToneColors => {
  if (state === 'resting') {
    return {
      container: theme.colors.surfaceVariant,
      onContainer: theme.colors.onSurfaceVariant,
      accent: theme.colors.onSurfaceVariant,
    };
  }
  return STATE_TONES[state][theme.dark ? 'dark' : 'light'];
};

export const levelTone = (level: LimitLevel, theme: MD3Theme): ToneColors => {
  if (level === 'warning') return WARNING_TONE[theme.dark ? 'dark' : 'light'];
  if (level === 'critical' || level === 'reached') {
    return { container: theme.colors.errorContainer, onContainer: theme.colors.onErrorContainer, accent: theme.colors.error };
  }
  return {
    container: theme.colors.surfaceVariant,
    onContainer: theme.colors.onSurfaceVariant,
    accent: theme.colors.primary,
  };
};
